
import * as React from "react";

type Props = {
  children: React.ReactNode;
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
};

type State = {
  error: Error | null;
};

/**
 * Top-level error boundary. Catches render errors anywhere in the tree
 * and shows a recoverable error state.
 *
 * For Phase 2: extend to optionally report errors to a telemetry endpoint.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info);
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }
      return <DefaultErrorFallback error={this.state.error} reset={this.reset} />;
    }
    return this.props.children;
  }
}

function DefaultErrorFallback({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <div className="empty-shell" style={{ paddingTop: 80, maxWidth: 560, margin: "0 auto" }}>
      <h3>Something went wrong.</h3>
      <p style={{ marginTop: 12 }}>
        The app hit an unexpected error and stopped. Your saved initiatives are still in localStorage and should reappear when you try again.
      </p>
      <details style={{ marginTop: 16, fontSize: 12, color: "var(--muted-ink)", textAlign: "left" }}>
        <summary style={{ cursor: "pointer", marginBottom: 8 }}>Technical details</summary>
        <pre style={{ whiteSpace: "pre-wrap", fontFamily: "var(--font-geist-mono), monospace", fontSize: 11 }}>
          {error.message}
          {error.stack ? "\n\n" + error.stack.split("\n").slice(0, 6).join("\n") : ""}
        </pre>
      </details>
      <div style={{ marginTop: 20 }}>
        <button type="button" className="btn primary" onClick={reset}>Try again</button>
      </div>
    </div>
  );
}
